/** @jsxImportSource @emotion/react */
import React, { useCallback, useMemo, useState } from 'react';
import { queryCache, useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router';
import { PageWrapper } from 'components/page';
import PageHeader from '@atlaskit/page-header';
import Page from '@atlaskit/page';
import _ from 'lodash';
import { css } from '@emotion/react';
import Spinner from '@atlaskit/spinner';
import {
  Box,
  Container,
  HStack,
  Stack,
  VStack,
  Text,
  CloseButton,
  useDisclosure,
  SlideFade,
  Image,
  Fade, Slide, Heading, Input, Center, Flex
} from '@chakra-ui/react';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { AnimatePresence, motion } from 'framer-motion';
import { StringParam, useQueryParam } from 'use-query-params';
import TextArea from '@atlaskit/textarea';
import { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import Button, { ButtonGroup } from '@atlaskit/button';
import moment from 'moment';
import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import DocumentIcon from '@atlaskit/icon/glyph/document';
import UploadIcon from '@atlaskit/icon/glyph/upload';
import { useDropzone } from 'react-dropzone';
import Tooltip from '@atlaskit/tooltip';
import Zoom from 'react-medium-image-zoom';
import { CompanyAvatar } from '../../components/avatar';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from '../../components/i18n';
import api from '../../libs/api';
import MotionBox from '../../components/chakra/MotionBox';
import DetailCard from '../../components/chakra/DetailCard';
import DetailRow from '../../components/chakra/DetailRow';
import useEscape from '../../hooks/useEscape';
import { MoreDropdownMenu } from '../../components/dropdown-menu';
import { useAuth } from '../auth';
import SearchBar from '../../components/SearchBar';
import StatusFilter from '../sale/components/StatusFilter';
import { DatePicker } from '../../components/date-picker';
import useApiQuery from '../../hooks/useApiQuery';
import { Customer } from '../sale/types';
import 'react-medium-image-zoom/dist/styles.css';
import { Link } from 'react-router-dom';
import { calculateVatAmount } from '../sale/invoiceHelpers';
import MobileIcon from '@atlaskit/icon/glyph/mobile';
import ScreenIcon from '@atlaskit/icon/glyph/screen';
import EmailIcon from '@atlaskit/icon/glyph/email';
import QuestionIcon from '@atlaskit/icon/glyph/question';

const DocumentSourceIcon = ({ source, ...other }) => {
  switch (source) {
    case 'email':
      return <EmailIcon label="via e-mail" {...other} />;
    case 'mobile':
      return <MobileIcon label="via mobiele app" {...other} />;
    case 'webapp':
      return <ScreenIcon label="via webapp" {...other} />;
    case 'system':
      return <ScreenIcon label="via webapp" {...other} />;
    default:
      return null;
  }
};
DocumentSourceIcon.defaultProps = {
  size: 'medium',
  weight: 'normal'
};

const parseDescription = (description) => {
  description = description.replaceAll('NOTPROVIDED<br>', '').replaceAll('NOTPROVIDED\n', '').replaceAll(/, NOT_?PROVIDED/gi, '');
  description = description.replaceAll(/(omschrijving: .*)\n(.*\nIBAN)/gi, '$1 $2');
  if (!description.includes('\n')) {
    description = description.replaceAll(/ ([a-z]+?: ?)/gi, '\n$1');
  } else {
    description = description.replaceAll(/(([a-z ]+): .+)/gi, '\n$1');
  }
  return description;
};

const cleanDescription = (description) => {
  description = parseDescription(description);
  if (!description) {
    return null;
  }
  const match = description.match(/(omschrijving: ?((?:\n|<br>|.)+?))(?:(?:\b.+):|$)/i);
  if (!match) {
    return description;
  }
  return match[2].trim();
};

const descriptionParts = (tx) => {
  const {description} = tx;
  const parsed = parseDescription(description);
  if (!parsed) {
    return [];
  }
  const parts = parsed.split(/(?:\n|<br>)/).map((part) => part.trim()).filter((part) => part);

  let out = [];
  const other = [];

  for (const part of parts) {
    const match = part.match(/(?:^|\b)(.*?[a-z]{2,}?): ?((?:.|\n)+)$/i);
    if (match) {
      out.push({key: match[1], value: match[2]});
    } else {
      other.push(part);
    }
  }


  if (other.length) {
    const key = out.find((part) => part.key === 'Omschrijving') ? 'Overig' : 'Omschrijving';
    out.push({key, value: other.join('\n')});
  }

  out = _.orderBy(out, (part) => ({'Naam': 1, 'Omschrijving': 2, 'IBAN': 3}[part.key] || 3));

  if (!out.find((part) => part.key === 'Naam')) {
    out= [{key: 'Omschrijving', value: description}];
  }

  // out.push({key: 'Naam', value: tx.accountName });
  out.push({key: 'Rekeningnummer', value: tx.accountNumber });
  out.push({key: 'Referentiecode', value: tx.referenceCode });

  out = _.uniqBy(out, (part) => part.value);
  out = out.filter((part) => part.key !== 'Naam');
  out = out.filter((part) => !!part.value);

  return out;
};


const ABImage = ({ attachmentId, height = 500, ...other }) => {
  const {accessToken} = useAuth();

  const url = `https://api.accountingbox.nl/attachments/${  attachmentId}/download?accessToken=${accessToken}`;
  let thumbnail = `https://images.accountingbox.nl/thumbnail?type=png&url=${encodeURIComponent(url)}&height=${height}`;
  if (!attachmentId) {
    thumbnail = 'assets/images/file.png';
  }
  return ( <Zoom zoomImg={{src:`${url}&format=full`}}><Image src={thumbnail} {...other} fallbackSrc="assets/images/file.png" /></Zoom>);
};

const AmountFormatter = ({ amount, currency }) => {
  return (
    <>
      {currency !== 'EUR' && (
      <span css={css`padding-right: 4px;`}>
        {currency}
      </span>
      )}
      {amount > 0 && '+'}
      <FormattedNumber value={amount} />
    </>
  );
};
const BankTransactionsPage2 = (props) => {
  const { bankAccountId } = useParams();
  const [selectedTxId, setSelectedTxId] = useQueryParam('id', StringParam);
  const { queryParams, debouncedUpdateQueryParams, updateQueryParams, handleChangeQueryParam } = useApiQuery();
  const {search: searchQ, dateTo, dateFrom, status} = queryParams;
  const [search, setSearch] = useState(searchQ);

  const handleEscape = useCallback(() => {
    setSelectedTxId(null);
  }, [setSelectedTxId]);

  const isOpen = !!selectedTxId;

  useEscape(handleEscape);
  const intl = useIntl();
  const { isBookkeeper } = useAuth();

  const [onDrop, { isLoading: isUploading }] = useMutation(
    async (acceptedFiles) => {
      console.log(acceptedFiles);
      const formData = new FormData();
      formData.append('file', acceptedFiles[0]);
      try {
        const response = await api.post(
          `attachments/upload?bankTransactionId=${selectedTxId}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'source': 'webapp'
            },
            // onUploadProgress,
          }
        );
      } catch (e) {
        console.log(e);

      }
    },
    {
      onSuccess: () => {
        queryCache.invalidateQueries(['bank-transactions', bankAccountId]);
        queryCache.invalidateQueries(['bank-transactions', selectedTxId]);
      }
    }
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: {'image/*':[], 'application/pdf': []},
    onDrop,
    multiple: false,
  });

  const handleSearch = React.useCallback(
    (value) => {
      setSearch(value);
      debouncedUpdateQueryParams({ search: value, page: 1});
    },
    [setSearch, debouncedUpdateQueryParams]
  );
  const resetSearch = React.useCallback(
    () => {
      setSearch('');
      debouncedUpdateQueryParams({ search: '', page: 1});
    },
    [setSearch, debouncedUpdateQueryParams]
  );

  const { data: bankAccount, isLoading: isLoadingBankAccount } = useQuery(['BankAccount', bankAccountId], async (key) => api.get(`BankAccounts/${bankAccountId}`));

  const {data: bankTransaction} = useQuery(['bank-transactions', selectedTxId], async (key) => api.get(`BankTransactions/${selectedTxId}`, {
    params: {
      filter: {
        'include': [
          {
            relation: 'attachment',
            scope: {
              fields: {
                id: true,
                created: true
              }
            }
          },
          'bankRule', 'user', {
            'relation': 'payments',
            'scope': {
              'include': {
                'relation': 'invoice',
                'scope': {
                  'include': [
                    {
                      relation: 'Attachment',
                      scope: {
                        fields: {
                          id: true,
                          source: true,
                          created: true
                        }
                      }
                    },
                    {
                      'relation': 'vendor',
                      'scope': { 'fields': { 'name': true, 'website': true } }
                    }, { 'relation': 'customer', 'scope': { 'fields': { 'name': true, 'website': true } } }]
                }
              }
            }
          }]
      }
    }
  }), {
    enabled: !!selectedTxId,
  });

  const {
    data,
    error,
    isLoading,
    isFetching
  } = useQuery(['bank-transactions', bankAccountId], async (key) => api.get('BankTransactions', {
    params: {
      filter: {
        'where': {
          bankAccountId,
          'date': { 'gte': '2022-01-01T00:00:00.000Z' }
        },
        'order': 'date DESC',
        'include':
          [
            {
              relation: 'attachment',
              scope: {
                fields: {
                  id: true,
                  source: true,
                  created: true
                }
              }
            },
            'bankRule', 'user', {
              'relation': 'payments',
              'scope': {
                'include': {
                  'relation': 'invoice',
                  'scope': {
                    'include': [
                      {
                        relation: 'Attachment',
                        scope: {
                          fields: {
                            id: true,
                            source: true,
                            created: true
                          }
                        }
                      },
                      {
                        'relation': 'vendor',
                        'scope': { 'fields': { 'name': true, 'website': true } }
                      }, { 'relation': 'customer', 'scope': { 'fields': { 'name': true, 'website': true } } }]
                  }
                }
              }
            }]
      }
    }
  }));

  const tx = useMemo(() => {
    if (!selectedTxId) return null;
    if (bankTransaction) return bankTransaction;
    return data?.find((tx) => tx.id === selectedTxId);
  }, [selectedTxId, data, bankTransaction]);

  const txByDate = useMemo(() => {

    return _.groupBy(data, (tx) => moment(tx.date).format('YYYY-MM-DD'));

  }, [data]);

  const breadcrumbs = (
    <Breadcrumbs onExpand={()=>{}}>
      <BreadcrumbsItem text="Bank accounts" key="Bank accounts" />
      <BreadcrumbsItem text={bankAccount?.bankAccountNumber} key={bankAccount?.bankAccountNumber} />
    </Breadcrumbs>
  );

  const actionsContent = useMemo(() => {
    return (
      <ButtonGroup>
        <MoreDropdownMenu>
          <>
            <DropdownItem onClick={() => null}>
              <FormattedMessage
                id="bank.BankTransactionsPage.importTransactions"
                defaultMessage="Import transactions"
              />
            </DropdownItem>
          </>
        </MoreDropdownMenu>
      </ButtonGroup>
    );
  }, [isBookkeeper]);

  const barContent = React.useMemo(() => {
    const searchLabel = intl.formatMessage({
      id: 'sale.PurchasesPage.search',
      defaultMessage: 'Search',
      description: 'Search placeholder',
    });

    const dateFromPlaceholder = intl.formatMessage({
      id: 'sale.SalesPage.dateFromPlaceholder',
      defaultMessage: 'From',
    });
    const dateToPlaceholder = intl.formatMessage({
      id: 'sale.SalesPage.dateToPlaceholder',
      defaultMessage: 'To',
    });

    const itemsContainer = css`
      display: flex;
      align-items: center;
      margin-top: 1rem;
      margin-bottom: 1rem;
      && > div:not(:first-of-type) {
        margin-left: 8px;
      }
    `;

    return (
      <div>
        <div css={itemsContainer}>
          <div css={css`flex: 0 0 240px;`}>
            <SearchBar
              placeholder={searchLabel}
              value={search}
              onChange={handleSearch}
              onReset={resetSearch}
            />
          </div>
          {/* <div css={css`flex: 0 0 100px;`}> */}
          {/*  <StatusFilter value={status} onChange={handleChangeQueryParam('status')} options={['unpaid', 'paid','partiallypaid']} /> */}
          {/* </div> */}
          <div>{isFetching && <Spinner />}</div>
        </div>
      </div>
    );
  }, [intl, isFetching, search, searchQ]);

  return (
    <div css={css`
//background: white;
background: #FAFBFC;
      min-height: 100vh;`}
    >
      <PageWrapper fullWidth>

        <MotionBox
          variants={{
            open: { paddingRight: 240 },
            closed: { paddingRight: 0 } }}
          transition={{ duration: 0.3, ease: [0.15, 0.5, 0.5, 1]}}
          initial="closed"
          animate={isOpen ? 'open' : 'closed'}
        >
          <Container>
            <Page>
              <PageHeader actions={actionsContent} bottomBar={barContent}>
                <FormattedNumber value={bankAccount?.balance||0} style='currency' currency={bankAccount?.currency||'EUR'} />
                <Text>{bankAccount?.bankAccountNumber}</Text>
              </PageHeader>
              {error && (
              <div>
                Error:
                {error.message}
              </div>
              )}
              {data && (
              <Box maxW="lg" mt={10}>
                <VStack spacing={8}>
                  {Object.entries(txByDate).slice(0, 40).map(([date, transactions]) => {
                    return (
                      <Box key={date} w="100%">
                        <Box mb={2} color="var(--ds-text-lowEmphasis,#6B778C)" fontSize="sm">
                          <FormattedDate
                            value={date}
                            day="numeric"
                            month="short"
                          />
                        </Box>
                        {/* box-shadow: rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px; background: white;` */}
                        <Stack
                          boxShadow="base"
                          width="100%"
                          maxW="xl"
                          spacing={0}
                          borderRadius="lg"
                          p={1}
                          bg="white"
                          position="relative"
                        >
                          {transactions.map((tx) => {
                            return (
                              <HStack
                                key={tx.id}
                                bg="white"
                                p="16px"
                                alignItems="center"
                                justifyContent="space-between"
                                width="100%"
                                borderRadius="lg"
                                cursor="pointer"
                                bg={selectedTxId === tx.id ? 'rgba(19, 193, 129, 0.05)' : 'white'}
                                _hover={{
                                  background: selectedTxId === tx.id ? 'rgba(19, 193, 129, 0.05)' : '#FAFBFC'
                                }}
                                _active={{
                                  background: 'rgba(19, 193, 129, 0.05)'
                                }}
                                onClick={() => {
                                  setSelectedTxId(tx.id);
                                }}
                              >
                                <HStack overflow="hidden" spacing="16px" flexGrow={1} flexShrink={1} flexBasis="auto">
                                  <Box lineHeight={1}>
                                    <CompanyAvatar size="medium" company={{ name: tx.accountName || cleanDescription(tx.description) }} />
                                  </Box>
                                  <VStack overflow="hidden" spacing="4px" alignItems="flex-start">
                                    <Text
                                      w="100%"
                                      lineHeight={1.25}
                                      maxW="xs"
                                      isTruncated
                                      fontSize="sm"
                                      fontWeight="normal"
                                    >
                                      {tx.accountName || cleanDescription(tx.description)}
                                    </Text>
                                    {(tx.accountName && tx.description || tx.accountNumber) &&(
                                    <Text
                                      lineHeight={1.25}
                                      w="100%"
                                      maxW="xs"
                                      isTruncated
                                      fontSize="xs"
                                      color="gray.500"
                                    >
                                      {tx.accountName && cleanDescription(tx.description) || tx.accountNumber}
                                    </Text>
                                    )}
                                  </VStack>
                                </HStack>
                                <Box flex={0} flexShrink={0}>
                                  <Text fontSize='sm' fontWeight="normal" whiteSpace="nowrap">
                                    <AmountFormatter amount={tx.amount} currency={tx.currency} />
                                  </Text>
                                </Box>
                                {!tx.processed && !tx.attachment && (
                                  <Tooltip delay={0} content={<span>Ontbrekende informatie</span>}>
                                    <Box position="absolute" right={-7} bg="red.500" height={2} width={2} color='white' borderRadius="100%" />
                                  </Tooltip>
                                )}
                                {!tx.processed  && tx.attachment && (
                                  <Tooltip delay={0} content={<span>In behandeling</span>}>
                                    <Box position="absolute" right={-7} bg="orange.300" height={2} width={2} color='white' borderRadius="100%" />
                                  </Tooltip>
                                )}
                                {/* {tx.processed && <Box position={'absolute'} left={'-180px'}>GB 5848484 <Box fontSize={'sm'} gray={'gray.500'}>(bankregel)</Box></Box>} */}
                              </HStack>
                            );
                          })}
                          ;
                        </Stack>
                      </Box>
                    );
                  })}
                </VStack>
              </Box>
              )}
            </Page>
          </Container>
        </MotionBox>
        <AnimatePresence>

          {isOpen && tx && (
          <MotionBox
            initial={{ opacity: 0, x: '50%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '50%' }}
            transition={{ duration: 0.3, ease: [0.15, 0.5, 0.5, 1]}}
            right={0}
            top={0}
            bottom={0}
            position='fixed'
            height="100vh"
            w="500px"
            p={7}
            zIndex={2}
          >
            <Box
              overflow="scroll"
              boxShadow="rgb(25 28 31 / 5%) 0px 0.125rem 0.25rem, rgb(25 28 31 / 10%) 0px 0.1875rem 1rem"
              borderRadius='lg'
              w='100%'
              height='100%'
              bg="#FAFBFC"
              p={4}
            >
              <Button
                position="absolute"
                iconBefore={<CrossIcon size="small" label="Remove" />}
                appearance="subtle"
                onClick={()=> {
                  setSelectedTxId(null);
                }}
              />
              <VStack alignItems="center">
                <CompanyAvatar size="medium" company={{ name: tx.accountName || tx.description }} />
                <Heading size="sm" textAlign="center">
                  {tx.accountName || tx.description}
                </Heading>
                <Heading pt={3} size="lg" fontWeight="semibold">
                  <AmountFormatter amount={tx.amount} currency={tx.currency} />
                </Heading>
                <Box fontWeight="medium" color="gray.500" maxW="md">
                  <FormattedDate
                    value={tx.date}
                    day="numeric"
                    month="short"
                  />
                </Box>
              </VStack>
              <DetailCard>
                <>
                  {descriptionParts(tx).map(row => {
                    return <DetailRow name={row.key} value={row.value} />;
                  })}
                </>
              </DetailCard>
              {!tx.processed && !tx.attachment  && (
              <Flex borderRadius="lg" my={8} height="2xs" bg="white" boxShadow='base' w="100%" p={6} color="gray.500" {...getRootProps()}>
                <Flex
                  onClick={open}
                  direction="column"
                  _hover={{
                    background: 'rgba(19, 193, 129, 0.05)',
                    // background: 'var(--ds-background-input-hovered, #EBECF0)',
                    borderColor: 'var(--ds-border-focused, #78F0C4)'
                  }}
                  transition="all 0.2s ease-in-out"
                  cursor="pointer"
                  borderRadius="lg"
                  bg='#FAFBFC'
                  border="2px var(--ds-border-input, #DFE1E6) dashed"
                  flex='1'
                  alignItems="center"
                  justifyContent="center"
                >
                  {isUploading ? <Spinner /> : (
                    <>
                      <Box><DocumentIcon /></Box>
                      <Box>Upload bon of factuur</Box>
                      <input {...getInputProps()} />
                    </>
                  )}
                </Flex>
              </Flex>
              )}

              {tx.attachment && (
                <>
                  <Box borderRadius="lg" my={8} minHeight="3xs" bg="white" boxShadow='base' w="100%" p={6} color="gray.500">
                    <Center><ABImage borderRadius="lg" attachmentId={tx.attachment.id} maxHeight="2xs" fit="contain" boxShadow="base" maxW="100%" /></Center>
                    <Box mt={3} textAlign="center" fontSize="sm"><FormattedDate value={tx.attachment.created} hour="numeric" minute="numeric" /></Box>
                  </Box>
                </>
              )}


              {tx.payments?.length > 0 && (
                <>
                  <Box mt={8} mb={4} fontSize="sm" textAlign={'center'} fontWeight="normal" color="gray.500">Gekoppeld aan factuur</Box>
                    {tx.payments.map(p=> (
                      <Box key={p.invoiceId} spacing={6} borderRadius="lg" mb={8} minHeight="3xs" bg="white" boxShadow='base' w="100%" p={6}>
                        <DetailCard boxShadow='none' p={0} w="100%" mt={0} pt={2}>
                          <DetailRow
                            name={p.invoiceType==='Purchase' ? 'Leverancier' : 'Klant'}
                            value={(
                              <HStack spacing={1}>
                                <Box color="initial" lineHeight={1} marginY={-4}><CompanyAvatar size="small" company={p.invoice?.customer || p.invoice?.vendor} /></Box>
                                <Box>{(p.invoice?.customer || p.invoice?.vendor).name}</Box>
                              </HStack>
                          )}
                          />
                          <DetailRow
                            name="Factuurnummer"
                            value={(
                              <Link target="_blank" to={p.invoiceType === 'Sale' ? `../verkopen/${p.invoiceId}/bewerk` : `../purchases/${p.invoiceId}`}>
                                {p.invoice?.invoiceNumber}
                                {' '}
                              </Link>
)}
                          />
                          <DetailRow name="Betaalbedrag" value={<AmountFormatter amount={p.amount} />} />
                        </DetailCard>
                        <Center><ABImage borderRadius="lg" attachmentId={p.invoice?.Attachment?.id} maxHeight="2xs" fit="contain" boxShadow="base" maxW="100%" /></Center>
                        <HStack spacing={1} mt={3} alignItems="center" justifyContent='center' textAlign="center" color="gray.500">
                          <Box><FormattedDate value={p.invoice?.Attachment?.created} hour="numeric" minute="numeric" /></Box>
                          <Box lineHeight={1} opacity={0.8}><DocumentSourceIcon source={p.invoice?.Attachment?.source} /></Box>
                        </HStack>
                      </Box>
                    )
                    )}
                </>
              )}

              {tx.journalEntry?.Lines?.length > 0 && (
                <>
                  <Box mt={8} mb={4} fontSize="sm" textAlign={'center'} fontWeight="normal" color="gray.500">Rechtstreeks geboekt</Box>

              <DetailCard  mt={0}>
                {tx.journalEntry?.Lines.map(l=> (
                  <>
                    <DetailRow

                      name={l.account.description}
                      description={l.account.accountNumber}
                      value={<AmountFormatter amount={l.amount} />}
                    />
                    {l.vatCode && <DetailRow name={l.vatCode.description} description={l.vatCode.id} value={<AmountFormatter amount={calculateVatAmount(l.amount, l.vatCode)} />} />}
                  </>
                ))}
              </DetailCard>
                </>
              )}


              <DetailCard>
                <TextArea appearance="subtle" placeholder="Voeg notitie toe" />
              </DetailCard>
            </Box>
          </MotionBox>
          )}
        </AnimatePresence>
      </PageWrapper>
    </div>
  )
  ;

};

export default BankTransactionsPage2;
